import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, ArrowRight, ArrowLeft, CheckCircle, User, Building2,
  Phone, Mail, MapPin, Globe, Briefcase, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country list (simplified)
const COUNTRIES = [
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'DE', name: 'Germany' },
  { code: 'PL', name: 'Poland' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'OTHER', name: 'Other' }
];

// Languages
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'ru', name: 'Russian' },
  { code: 'de', name: 'German' },
  { code: 'pl', name: 'Polish' }
];

/**
 * Provider Registration Page
 * Multi-step form for providers to self-register
 */
const ProviderRegisterPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    provider_type: 'individual',
    company_name: '',
    service_categories: [],
    country: '',
    city: '',
    languages: ['en'],
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/service-mvp/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      service_categories: prev.service_categories.includes(categoryId)
        ? prev.service_categories.filter(c => c !== categoryId)
        : [...prev.service_categories, categoryId]
    }));
  };

  const toggleLanguage = (langCode) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(langCode)
        ? prev.languages.filter(l => l !== langCode)
        : [...prev.languages, langCode]
    }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        setError('Please fill in all required fields');
        return false;
      }
      if (!formData.email.includes('@')) {
        setError('Please enter a valid email address');
        return false;
      }
    }
    
    if (step === 2) {
      if (!formData.password || formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.provider_type === 'company' && !formData.company_name) {
        setError('Please enter your company name');
        return false;
      }
    }
    
    if (step === 3) {
      if (formData.service_categories.length === 0) {
        setError('Please select at least one service category');
        return false;
      }
    }
    
    if (step === 4) {
      if (!formData.country || !formData.city) {
        setError('Please enter your location');
        return false;
      }
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
      setError(null);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/providers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          provider_type: formData.provider_type,
          company_name: formData.provider_type === 'company' ? formData.company_name : null,
          service_categories: formData.service_categories,
          country: formData.country,
          city: formData.city,
          languages: formData.languages,
          description: formData.description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      
      // Store session token
      if (data.session_token) {
        localStorage.setItem('provider_session', data.session_token);
      }
      
      toast.success('Registration successful!');
      
      // Move to success step
      setStep(5);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step indicators
  const steps = [
    { num: 1, title: 'Contact', icon: Mail },
    { num: 2, title: 'Account', icon: User },
    { num: 3, title: 'Services', icon: Briefcase },
    { num: 4, title: 'Location', icon: MapPin }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <h1 className="text-3xl font-bold text-orange-600">KEIKKA</h1>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900">
            {step === 5 ? 'Welcome Aboard!' : 'Become a Provider'}
          </h2>
          <p className="text-gray-600 mt-2">
            {step === 5 
              ? 'Your account has been created successfully' 
              : 'Start receiving service requests in your area'}
          </p>
        </div>

        {/* Step Progress */}
        {step < 5 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-full ${
                    step >= s.num 
                      ? 'bg-orange-100 text-orange-700' 
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <s.icon className="h-4 w-4" />
                  <span className="text-sm font-medium hidden sm:inline">{s.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-8 h-0.5 ${step > s.num ? 'bg-orange-400' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <Card>
          <CardContent className="pt-6">
            {/* Step 1: Contact Info */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-contact">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    placeholder="Your full name"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="you@example.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input 
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="+372 5555 1234"
                    data-testid="input-phone"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll use this for SMS notifications about new requests
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Account Type & Password */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-account">
                <div>
                  <Label className="mb-2 block">Account Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => updateForm('provider_type', 'individual')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.provider_type === 'individual'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <User className={`h-6 w-6 mb-2 ${formData.provider_type === 'individual' ? 'text-orange-500' : 'text-gray-400'}`} />
                      <div className="font-medium">Individual</div>
                      <div className="text-xs text-gray-500">Freelancer or sole trader</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateForm('provider_type', 'company')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.provider_type === 'company'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <Building2 className={`h-6 w-6 mb-2 ${formData.provider_type === 'company' ? 'text-orange-500' : 'text-gray-400'}`} />
                      <div className="font-medium">Company</div>
                      <div className="text-xs text-gray-500">Business or organization</div>
                    </button>
                  </div>
                </div>

                {formData.provider_type === 'company' && (
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input 
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => updateForm('company_name', e.target.value)}
                      placeholder="Your company name"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input 
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => updateForm('password', e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="input-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input 
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => updateForm('confirmPassword', e.target.value)}
                    placeholder="Repeat your password"
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Service Categories */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-services">
                <Label className="block mb-3">Select Your Services *</Label>
                <p className="text-sm text-gray-500 mb-4">
                  Choose all the service categories you can provide
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.id)}
                      className={`p-3 rounded-lg border-2 text-left text-sm transition-all ${
                        formData.service_categories.includes(cat.id)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {formData.service_categories.includes(cat.id) && (
                          <CheckCircle className="h-4 w-4 text-orange-500" />
                        )}
                        <span className="font-medium">{cat.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
                
                {formData.service_categories.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600">
                      Selected: {formData.service_categories.length} categories
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Location & Languages */}
            {step === 4 && (
              <div className="space-y-4" data-testid="step-location">
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(value) => updateForm('country', value)}
                  >
                    <SelectTrigger data-testid="select-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input 
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateForm('city', e.target.value)}
                    placeholder="Your city"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Languages You Speak</Label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => toggleLanguage(lang.code)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          formData.languages.includes(lang.code)
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">About You (Optional)</Label>
                  <Textarea 
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Tell clients about your experience and services..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <div className="text-center py-8" data-testid="step-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  You're All Set!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your provider account is active. You'll start receiving notifications 
                  when clients need services in your categories.
                </p>
                
                <div className="bg-orange-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-orange-800 mb-2">Next Steps</h4>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Complete your profile</li>
                    <li>• Choose a subscription plan for SMS notifications</li>
                    <li>• Start responding to requests!</li>
                  </ul>
                </div>
                
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" asChild>
                    <Link to="/">Go to Homepage</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/provider/dashboard">
                      Provider Dashboard
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Navigation Buttons */}
            {step < 5 && (
              <div className="flex justify-between mt-6 pt-4 border-t">
                {step > 1 ? (
                  <Button variant="ghost" onClick={prevStep}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 4 ? (
                  <Button onClick={nextStep} data-testid="btn-next">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-gradient-to-r from-orange-500 to-yellow-500"
                    data-testid="btn-register"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Shield className="h-4 w-4 mr-2" />
                    )}
                    Create Account
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Already have account */}
        {step < 5 && (
          <p className="text-center text-sm text-gray-600 mt-6">
            Already have a provider account?{' '}
            <Link to="/provider/login" className="text-orange-600 hover:underline">
              Sign in here
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default ProviderRegisterPage;
